/**
 * parts/gear.js — held tools and the targets they are used against.
 *
 * Mallets, the cork rifle, duck targets, rubber ducks, the fishing rod and the
 * pusher coin. Same rules as `props.js`: collider numbers are GDD-locked, and
 * anything that becomes a dynamic body keeps its orientation in the geometry
 * rather than on the object.
 *
 * @module assets3d/parts/gear
 */

import * as THREE from 'three';
import { ART, rngFor } from '../palette.js';
import { woodGrain, bullseye, coinFace } from '../textures.js';
import { geo, mat, mesh, makeAnchor } from './core.js';

/**
 * A mallet.
 * @param {'high-striker'|'mole'} kind Heavy two-hander, or a light one-hander.
 * @returns {THREE.Group} with `userData.headOffset`, `holdOffset`, `collider`.
 */
export function createMallet(kind = 'mole') {
  const heavy = kind === 'high-striker';
  const g = new THREE.Group();
  g.name = `mallet-${kind}`;

  const handleLen = heavy ? 0.95 : 0.34;
  const headR = heavy ? 0.09 : 0.055;

  const handle = mesh(
    geo(`mallet-handle-${handleLen}`, () => new THREE.CylinderGeometry(
      0.021, 0.026, handleLen, 10,
    )),
    mat(0xffffff, { rough: 0.82, map: woodGrain(ART.woodLight) }),
  );
  handle.position.y = handleLen / 2;
  g.add(handle);

  // Leather grip wrap at the bottom of the shaft.
  const grip = mesh(
    geo(`mallet-grip-${handleLen}`, () => new THREE.CylinderGeometry(
      0.028, 0.030, handleLen * 0.34, 10,
    )),
    mat(0x6b4a2f, { rough: 0.92 }),
  );
  grip.position.y = handleLen * 0.17;
  g.add(grip);

  const collar = mesh(
    geo('mallet-collar', () => new THREE.CylinderGeometry(0.030, 0.030, 0.022, 10)),
    mat(ART.gold, { rough: 0.4, metal: 0.6 }),
  );
  collar.position.y = handleLen * 0.35;
  g.add(collar);

  // The head lies across the shaft, so its cylinder axis runs along X.
  const headGeo = geo(`mallet-head-${headR}`, () => {
    const c = new THREE.CylinderGeometry(headR, headR, headR * 2.1, 14);
    c.rotateZ(Math.PI / 2);
    return c;
  });
  const head = mesh(
    headGeo,
    heavy
      ? mat(0x5f6470, { rough: 0.5, metal: 0.55 })
      : mat(ART.red, { rough: 0.62 }),
  );
  head.position.y = handleLen;
  head.name = 'head';
  g.add(head);

  // Banded ends: iron hoops on the striker, cream stripes on the mole mallet.
  const bandMat = heavy
    ? mat(ART.iron, { rough: 0.45, metal: 0.7 })
    : mat(ART.cream, { rough: 0.6 });
  for (const s of [-1, 1]) {
    const band = mesh(
      geo(`mallet-band-${headR}`, () => {
        const c = new THREE.CylinderGeometry(headR * 1.06, headR * 1.06, headR * 0.30, 14);
        c.rotateZ(Math.PI / 2);
        return c;
      }),
      bandMat,
      { receive: false },
    );
    band.position.set(s * headR * 0.88, handleLen, 0);
    g.add(band);
  }

  g.userData.headOffset = new THREE.Vector3(0, handleLen, 0);
  g.userData.collider = {
    shape: 'capsule', radius: headR, halfHeight: headR,
    mass: heavy ? 3.5 : 0.5, friction: 0.6, restitution: 0.25, events: true,
  };
  g.userData.holdOffset = new THREE.Vector3(0, -handleLen * 0.35, 0);
  return g;
}

/**
 * A cork rifle for the Duck Gallery. Barrel points along −Z.
 * @returns {THREE.Group} with `userData.muzzle` and `userData.holdOffset`.
 */
export function createRifle() {
  const g = new THREE.Group();
  g.name = 'rifle';
  const timber = mat(0xffffff, { rough: 0.72, map: woodGrain(ART.wood) });

  // Butt, comb and wrist, as three blocks rather than one slab — a rifle stock
  // has a profile, and the silhouette is what a player sees down the barrel.
  const butt = mesh(new THREE.BoxGeometry(0.052, 0.105, 0.13), timber);
  butt.position.set(0, -0.035, 0.245);
  g.add(butt);

  const wrist = mesh(new THREE.BoxGeometry(0.046, 0.070, 0.13), timber);
  wrist.position.set(0, -0.012, 0.125);
  g.add(wrist);

  const comb = mesh(new THREE.BoxGeometry(0.050, 0.035, 0.10), timber);
  comb.position.set(0, 0.026, 0.20);
  g.add(comb);

  const foreEnd = mesh(new THREE.BoxGeometry(0.050, 0.052, 0.22), timber);
  foreEnd.position.set(0, -0.006, -0.05);
  g.add(foreEnd);

  const barrel = mesh(
    geo('rifle-barrel', () => {
      const c = new THREE.CylinderGeometry(0.0135, 0.0145, 0.52, 12);
      c.rotateX(Math.PI / 2);
      return c;
    }),
    mat(0x3a3f48, { rough: 0.28, metal: 0.8 }),
  );
  barrel.position.set(0, 0.022, -0.22);
  g.add(barrel);

  // The painted band that makes it read as a fairground toy, not a firearm.
  const band = mesh(
    geo('rifle-band', () => {
      const c = new THREE.CylinderGeometry(0.0165, 0.0165, 0.045, 12);
      c.rotateX(Math.PI / 2);
      return c;
    }),
    mat(ART.red, { rough: 0.45 }),
  );
  band.position.set(0, 0.022, -0.40);
  g.add(band);

  const muzzleRing = mesh(
    geo('rifle-muzzle-ring', () => {
      const c = new THREE.CylinderGeometry(0.018, 0.018, 0.022, 12);
      c.rotateX(Math.PI / 2);
      return c;
    }),
    mat(ART.gold, { rough: 0.35, metal: 0.7 }),
  );
  muzzleRing.position.set(0, 0.022, -0.472);
  g.add(muzzleRing);

  const trigger = mesh(
    geo('rifle-trigger', () => new THREE.BoxGeometry(0.008, 0.030, 0.010)),
    mat(ART.gold, { rough: 0.4, metal: 0.6 }),
  );
  trigger.position.set(0, -0.048, 0.055);
  g.add(trigger);

  const sight = mesh(
    geo('rifle-sight', () => new THREE.BoxGeometry(0.008, 0.021, 0.012)),
    mat(ART.gold, { rough: 0.4, metal: 0.5 }),
  );
  sight.position.set(0, 0.048, -0.44);
  g.add(sight);

  // The cork, on its string. Nothing says "carnival rifle" more plainly.
  const cork = mesh(
    geo('rifle-cork', () => {
      const c = new THREE.CylinderGeometry(0.011, 0.013, 0.028, 10);
      c.rotateX(Math.PI / 2);
      return c;
    }),
    mat(0xd8b782, { rough: 0.95 }),
  );
  cork.position.set(0.028, -0.03, -0.44);
  g.add(cork);

  const string = new THREE.Line(
    new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(0, 0.022, -0.472),
      new THREE.Vector3(0.020, -0.008, -0.455),
      new THREE.Vector3(0.028, -0.030, -0.442),
    ]),
    new THREE.LineBasicMaterial({ color: 0xe8dcc4, transparent: true, opacity: 0.8 }),
  );
  string.name = 'cork-string';
  g.add(string);

  /** Muzzle transform — raycast origin for the trigger pull (GDD 6.5.1). */
  g.userData.muzzle = makeAnchor(g, 'muzzle', 0, 0.022, -0.48);
  g.userData.holdOffset = new THREE.Vector3(0, 0.02, 0.1);
  g.userData.cork = cork;
  return g;
}

/** The flat duck silhouette used by the gallery targets. */
function duckSilhouette() {
  return geo('duck-silhouette', () => {
    const s = new THREE.Shape();
    s.moveTo(0.105, 0.020);
    s.quadraticCurveTo(0.125, 0.100, 0.040, 0.115);
    s.lineTo(-0.028, 0.118);
    s.quadraticCurveTo(-0.050, 0.132, -0.056, 0.152);
    s.quadraticCurveTo(-0.062, 0.202, -0.096, 0.197);
    s.quadraticCurveTo(-0.126, 0.192, -0.126, 0.166);
    s.lineTo(-0.168, 0.153);
    s.lineTo(-0.123, 0.141);
    s.quadraticCurveTo(-0.106, 0.120, -0.086, 0.104);
    s.quadraticCurveTo(-0.055, 0.030, 0.005, 0.014);
    s.closePath();
    const g = new THREE.ExtrudeGeometry(s, {
      depth: 0.022, bevelEnabled: true,
      bevelThickness: 0.004, bevelSize: 0.004, bevelSegments: 1,
      curveSegments: 4,
    });
    g.translate(0, 0, -0.011);
    return g;
  });
}

/**
 * A metal duck silhouette on a conveyor (GDD 6.5.1).
 * @param {boolean} [golden]
 * @returns {THREE.Group} with `userData.bullseyeRadius`.
 */
export function createDuckTarget(golden = false) {
  const g = new THREE.Group();
  g.name = golden ? 'duck-target-golden' : 'duck-target';

  const plate = mesh(
    duckSilhouette(),
    golden
      ? mat(ART.gold, { rough: 0.22, metal: 0.85, emissive: 0x3a2a06, emissiveIntensity: 0.5 })
      : mat(ART.metal, { rough: 0.42, metal: 0.72 }),
  );
  g.add(plate);

  // Painted eye and bill so the plate reads as a duck at ten metres.
  const eye = mesh(
    geo('duckt-eye', () => new THREE.CircleGeometry(0.008, 10)),
    mat(ART.ink, { flat: true }),
    { cast: false, receive: false },
  );
  eye.position.set(-0.098, 0.172, 0.013);
  g.add(eye);

  const bill = mesh(
    geo('duckt-bill', () => {
      const s = new THREE.Shape();
      s.moveTo(-0.126, 0.166);
      s.lineTo(-0.168, 0.153);
      s.lineTo(-0.123, 0.141);
      s.closePath();
      return new THREE.ShapeGeometry(s);
    }),
    mat(0xf0a03a, { flat: true }),
    { cast: false, receive: false },
  );
  bill.position.z = 0.013;
  g.add(bill);

  const bull = mesh(
    geo('duckt-bull', () => new THREE.CircleGeometry(0.030, 20)),
    mat(0xffffff, { flat: true, map: bullseye(golden) }),
    { cast: false, receive: false },
  );
  bull.position.set(0.020, 0.062, 0.013);
  bull.name = 'bullseye';
  g.add(bull);

  if (golden) {
    // Sparkles pinned around the golden duck. Unlit quads, so they read as
    // glints regardless of where the stall light happens to be.
    const rnd = rngFor('golden-duck');
    const sparkMat = mat(0xffffff, {
      flat: true, transparent: true, opacity: 0.9, depthWrite: false,
    });
    for (let i = 0; i < 4; i++) {
      const s = mesh(geo('sparkle', () => sparkleGeo()), sparkMat, {
        cast: false, receive: false,
      });
      s.position.set(-0.14 + rnd() * 0.28, 0.02 + rnd() * 0.20, 0.02);
      s.scale.setScalar(0.6 + rnd() * 0.6);
      s.name = 'sparkle';
      g.add(s);
    }
  }

  g.userData.bullseyeRadius = 0.026;
  g.userData.golden = golden;
  return g;
}

/** A four-point glint. */
function sparkleGeo() {
  const s = new THREE.Shape();
  const R = 0.026, r = 0.005;
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2 - Math.PI / 2;
    const rr = i % 2 ? r : R;
    const p = [Math.cos(a) * rr, Math.sin(a) * rr];
    if (i === 0) s.moveTo(p[0], p[1]); else s.lineTo(p[0], p[1]);
  }
  s.closePath();
  return new THREE.ShapeGeometry(s);
}

/**
 * A floating rubber duck with a hookable head ring (GDD 6.7.1).
 * @param {'large'|'small'|'golden'} size
 * @returns {THREE.Group} with `userData.ringRadius` and `userData.ringAnchor`.
 */
export function createRubberDuck(size = 'large') {
  const ringR = { large: 0.038, small: 0.022, golden: 0.014 }[size] ?? 0.038;
  const g = new THREE.Group();
  g.name = `rubber-duck-${size}`;

  const golden = size === 'golden';
  const shell = golden
    ? mat(ART.gold, { rough: 0.18, metal: 0.7, emissive: 0x3a2a06, emissiveIntensity: 0.45 })
    : mat(0xffd166, { rough: 0.42 });

  const body = mesh(
    geo('rduck-body', () => {
      const b = new THREE.SphereGeometry(0.055, 14, 11);
      const pos = b.attributes.position;
      const v = new THREE.Vector3();
      for (let i = 0; i < pos.count; i++) {
        v.fromBufferAttribute(pos, i);
        // Flatten the underside into a waterline and sweep the tail up.
        if (v.y < 0) v.y *= 0.62;
        const t = Math.max(0, v.z / 0.055);
        v.y += t * t * 0.030;
        pos.setXYZ(i, v.x, v.y, v.z);
      }
      b.computeVertexNormals();
      return b;
    }),
    shell,
  );
  body.scale.set(1, 0.86, 1.24);
  body.position.y = 0.042;
  g.add(body);

  const head = mesh(
    geo('rduck-head', () => new THREE.SphereGeometry(0.036, 12, 10)),
    shell,
  );
  head.scale.set(1, 1.06, 1);
  head.position.set(0, 0.104, -0.046);
  g.add(head);

  // A short neck stops the head reading as a second ball balanced on the body.
  const neck = mesh(
    geo('rduck-neck', () => new THREE.CylinderGeometry(0.020, 0.028, 0.040, 10)),
    shell,
  );
  neck.position.set(0, 0.074, -0.036);
  g.add(neck);

  // The flat bill: a wedge, not a cone.
  const bill = mesh(
    geo('rduck-bill', () => {
      const c = new THREE.ConeGeometry(0.019, 0.038, 8);
      c.rotateX(-Math.PI / 2);
      c.scale(1.25, 0.5, 1);
      return c;
    }),
    mat(0xf5760f, { rough: 0.5 }),
  );
  bill.position.set(0, 0.098, -0.088);
  g.add(bill);

  for (const s of [-1, 1]) {
    const eye = mesh(
      geo('rduck-eye', () => new THREE.SphereGeometry(0.0062, 7, 6)),
      mat(ART.ink, { rough: 0.25 }),
      { cast: false },
    );
    eye.position.set(0.019 * s, 0.116, -0.070);
    g.add(eye);

    // Moulded wing ridges on the flanks.
    const wing = mesh(
      geo('rduck-wing', () => new THREE.SphereGeometry(0.024, 8, 6)),
      shell,
      { receive: false },
    );
    wing.scale.set(0.30, 0.72, 1.15);
    wing.position.set(0.046 * s, 0.046, 0.006);
    g.add(wing);
  }

  const ring = mesh(
    geo(`rduck-ring-${size}`, () => {
      const t = new THREE.TorusGeometry(ringR, Math.min(0.005, ringR * 0.22), 6, 18);
      t.rotateX(Math.PI / 2);
      return t;
    }),
    mat(golden ? ART.red : ART.teal, { rough: 0.4, metal: 0.2 }),
  );
  ring.position.set(0, 0.142, -0.046);
  ring.name = 'hook-ring';
  g.add(ring);

  g.userData.ringRadius = ringR;
  g.userData.ringAnchor = makeAnchor(g, 'ring-anchor', 0, 0.142, -0.046);
  g.userData.size = size;
  return g;
}

/**
 * A fishing rod with a hook on a line (GDD 6.7.1).
 * @returns {THREE.Group} with `userData.tip`, `hook`, `line`, `holdOffset`.
 */
export function createFishingRod() {
  const g = new THREE.Group();
  g.name = 'fishing-rod';

  // Split cane: three tapering sections with brass ferrules between them.
  const sections = [
    { z: -0.18, len: 0.36, r0: 0.0145, r1: 0.011 },
    { z: -0.54, len: 0.36, r0: 0.011, r1: 0.0075 },
    { z: -0.86, len: 0.28, r0: 0.0075, r1: 0.004 },
  ];
  const cane = mat(0xffffff, { rough: 0.66, map: woodGrain(0xc09a5e) });
  for (const s of sections) {
    const seg = mesh(
      geo(`rod-seg-${s.len}-${s.r0}`, () => {
        const c = new THREE.CylinderGeometry(s.r1, s.r0, s.len, 8);
        c.rotateX(Math.PI / 2);
        return c;
      }),
      cane,
    );
    seg.position.z = s.z;
    g.add(seg);
  }

  const brass = mat(ART.gold, { rough: 0.35, metal: 0.75 });
  for (const z of [-0.36, -0.72]) {
    const ferrule = mesh(
      geo('rod-ferrule', () => {
        const c = new THREE.CylinderGeometry(0.013, 0.013, 0.022, 8);
        c.rotateX(Math.PI / 2);
        return c;
      }),
      brass,
    );
    ferrule.position.z = z;
    g.add(ferrule);
  }

  // Line guides — small rings standing off the underside of the blank.
  for (const z of [-0.40, -0.66, -0.90]) {
    const guide = mesh(
      geo('rod-guide', () => {
        const t = new THREE.TorusGeometry(0.010, 0.0018, 5, 12);
        t.rotateY(Math.PI / 2);
        return t;
      }),
      brass,
      { receive: false },
    );
    guide.position.set(0, -0.016, z);
    g.add(guide);
  }

  const grip = mesh(
    geo('rod-grip', () => {
      const c = new THREE.CylinderGeometry(0.019, 0.021, 0.15, 10);
      c.rotateX(Math.PI / 2);
      return c;
    }),
    mat(0x8a6a45, { rough: 0.95 }),
  );
  grip.position.z = 0.06;
  g.add(grip);

  const tip = makeAnchor(g, 'rod-tip', 0, 0, -1.0);

  // Red-and-white bobber above the hook — the thing the player actually aims.
  const bobber = new THREE.Group();
  bobber.position.set(0, -0.20, -1.0);
  g.add(bobber);
  const bobTop = mesh(
    geo('bobber-top', () => new THREE.SphereGeometry(0.022, 10, 8, 0, Math.PI * 2, 0, Math.PI / 2)),
    mat(ART.red, { rough: 0.4 }),
  );
  bobber.add(bobTop);
  const bobBot = mesh(
    geo('bobber-bot', () => new THREE.SphereGeometry(0.022, 10, 8, 0, Math.PI * 2, Math.PI / 2, Math.PI / 2)),
    mat(ART.cream, { rough: 0.4 }),
  );
  bobber.add(bobBot);

  const hook = mesh(
    geo('rod-hook', () => {
      const t = new THREE.TorusGeometry(0.018, 0.0035, 6, 14, Math.PI * 1.45);
      t.rotateY(Math.PI / 2);
      return t;
    }),
    mat(ART.metal, { rough: 0.25, metal: 0.88 }),
  );
  hook.name = 'hook';
  hook.position.set(0, -0.35, -1.0);
  g.add(hook);

  const line = new THREE.Line(
    new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(0, 0, -1.0),
      new THREE.Vector3(0, -0.20, -1.0),
      new THREE.Vector3(0, -0.35, -1.0),
    ]),
    new THREE.LineBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.55 }),
  );
  line.name = 'line';
  g.add(line);

  g.userData.tip = tip;
  g.userData.hook = hook;
  g.userData.line = line;
  g.userData.bobber = bobber;
  g.userData.holdOffset = new THREE.Vector3(0, 0, 0.12);
  return g;
}

/**
 * A pusher-machine coin, struck with a goose head.
 * @returns {THREE.Mesh} with `userData.collider`.
 */
export function createCoin() {
  const m = mesh(
    geo('coin', () => new THREE.CylinderGeometry(0.019, 0.019, 0.0025, 20)),
    mat(0xffffff, { rough: 0.28, metal: 0.85, map: coinFace(ART.gold) }),
  );
  m.name = 'coin';
  m.userData.collider = {
    shape: 'cylinder', radius: 0.019, halfHeight: 0.00125,
    mass: 0.008, friction: 0.35, restitution: 0.05,
  };
  return m;
}
